import { request } from "./client";
import { registerMessages } from "./errors";

registerMessages({
  "failed to list payments": "Не удалось загрузить счета",
  "player_id, month (1-12), year required": "Выберите ребёнка и месяц",
  "amount must be > 0 (no group fee configured?)": "Укажите сумму больше нуля",
  "player has no linked parent": "У ребёнка нет привязанного родителя — счёт отправить некому",
  "expires_at must be RFC3339 timestamp": "Некорректный срок оплаты",
  "failed to create invoice": "Не удалось выставить счёт",
  "failed to create payment item": "Не удалось выставить счёт",
  "failed to cancel": "Не удалось отозвать счёт",
  "payment is in a terminal state and cannot be cancelled": "Этот счёт уже нельзя отозвать — обновите страницу",
});

export const INVOICE_STATUSES = [
  "pending",
  "processing",
  "completed",
  "manual",
  "failed",
  "expired",
  "cancelled",
  "refunded",
] as const;

const STATUS_LABELS: Record<string, string> = {
  pending: "Ждёт оплаты",
  processing: "Оплачивается",
  completed: "Оплачен",
  manual: "Оплачен наличными",
  failed: "Оплата не прошла",
  expired: "Просрочен",
  cancelled: "Отозван",
  refunded: "Деньги возвращены",
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

// Mirrors the server's cancel guard: paid money needs a refund, and a payment
// in processing may still go through at the bank.
const CANCELLABLE = new Set(["pending", "failed", "expired"]);

export function canCancel(status: string): boolean {
  return CANCELLABLE.has(status);
}

export interface InvoiceItem {
  id: string;
  payment_id: string;
  player_id: string;
  player_name?: string;
  month: number;
  year: number;
  amount: number;
}

export interface Invoice {
  id: string;
  amount: number;
  status: string;
  type?: string;
  payment_method: string;
  description?: string;
  expires_at?: string;
  paid_at?: string;
  admin_note?: string;
  player_names?: string;
  parent_names?: string;
  group_names?: string;
  items?: InvoiceItem[];
  created_at: string;
  updated_at: string;
}

export interface InvoiceFilters {
  status: string;
  groupID: string;
  month: string; // "YYYY-MM" or ""
}

export interface NewInvoice {
  playerID: string;
  month: string; // "YYYY-MM"
  amount: number;
  description: string;
  dueDate: string; // "YYYY-MM-DD" or ""
}

export const invoicesApi = {
  list: (filters: InvoiceFilters, offset: number, limit: number) => {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.groupID) params.set("group_id", filters.groupID);
    const [year, month] = filters.month.split("-");
    // The server applies the month filter only when both parts are present.
    if (year && month) {
      params.set("month", String(Number(month)));
      params.set("year", year);
    }
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    return request<Invoice[] | null>(`/api/payments/?${params}`);
  },

  issue: (input: NewInvoice) => {
    const [year, month] = input.month.split("-").map(Number);
    const description = input.description.trim();
    return request<Invoice>("/api/payments/invoice", {
      method: "POST",
      body: JSON.stringify({
        player_id: input.playerID,
        month,
        year,
        amount: input.amount,
        ...(description ? { description } : {}),
        // The club is in Vladivostok (the server's monthly winners job uses the
        // same zone), so "pay by the 5th" means the end of the 5th there.
        ...(input.dueDate ? { expires_at: `${input.dueDate}T23:59:59+10:00` } : {}),
      }),
    });
  },

  cancel: (id: string) =>
    request<{ ok: boolean }>(`/api/payments/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
};

const MONTHS = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

export function formatPeriod(month: number, year: number): string {
  return `${MONTHS[month - 1] ?? month} ${year}`;
}

export function invoicePeriods(items: InvoiceItem[] = []): string[] {
  return Array.from(new Set(items.map((i) => formatPeriod(i.month, i.year))));
}

const CLUB_DAY = new Intl.DateTimeFormat("ru-RU", {
  timeZone: "Asia/Vladivostok",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

export function formatClubDate(timestamp: string | null | undefined): string {
  if (!timestamp) return "—";
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? timestamp : CLUB_DAY.format(date);
}
